'use client'

import { CloseButton, Dialog, DialogPanel } from '@headlessui/react'
import CloseWhite from '@public/images/close_white.svg'
import Image from 'next/image'
import Script from 'next/script'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { configPublic } from '@src/config.public'
import { useQuerySelector } from '@src/hooks/useQuerySelector'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'

declare global {
  interface Window {
    WebVideoPlayerDOM: {
      launch: (props: LaunchVideoPlayerParams) => Promise<any>
      destroy: (instanceId?: string) => void
    }
  }
}

type CallbackFn = () => void

interface VideoCallbacks {
  onClose?: CallbackFn
  onPlay?: CallbackFn
  onPause?: CallbackFn
  onSeek?: CallbackFn
  closeCallback?: CallbackFn
  onAnyAction?: (action: { type: string; payload: any }) => void
  onProgress?: (progress: number) => void
  getAccessToken?: () => Promise<{ accessToken: string | undefined; tokenType?: string }>
}

interface PlayVideoProps extends VideoCallbacks {
  videoGUID: string
  userGUID?: string
  entityId?: string
  recommendationId?: string
  autoplay?: boolean
  configUrl?: string
  selector?: string
}

interface VideoPlayerContextType {
  playVideo: (playVideoProps: PlayVideoProps) => Promise<void>
  setScriptUrl: (url: string) => void
}

interface LaunchVideoPlayerParams {
  configUrl: string
  selector: string
  props: PlayVideoProps
}

const VIDEO_PLAYER_CONTAINER_ID = 'video-player-container'

const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(undefined)

export const VideoPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useSession()
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const [playerInstanceId, setPlayerInstanceId] = useState<string | null>(null)
  const [scriptUrl, setScriptUrl] = useState<string | undefined>(
    configPublic.videoPlayer?.scriptUrl
  )
  const [launchVideoParams, setLaunchVideoParams] = useState<LaunchVideoPlayerParams | null>(null)
  const launchVideoPlayerPromise = useRef<Promise<void>>(null)
  const containerElement = useQuerySelector(`#${VIDEO_PLAYER_CONTAINER_ID}`)
  const { locale } = useLocale()

  const closePlayer = () => {
    window.WebVideoPlayerDOM.destroy()
    setPlayerInstanceId(null)
    setLaunchVideoParams(null)
    launchVideoPlayerPromise.current = null
  }

  const playVideo = useCallback(
    async (playVideoProps: PlayVideoProps) => {
      if (!isScriptLoaded || !window.WebVideoPlayerDOM) {
        throw new Error('WebVideoPlayerDOM not loaded')
      }

      const getAccessToken = async () => {
        const newSession = await ServerActions.serial.getAuthSession()
        return {
          accessToken: newSession?.access_token,
          tokenType: newSession?.token_type,
        }
      }

      const params = {
        selector: `#${VIDEO_PLAYER_CONTAINER_ID}`,
        configUrl: playVideoProps.configUrl ?? configPublic.videoPlayer?.configUrl,
        props: {
          getAccessToken,
          userGUID: user?.guid,
          language: locale.codeSnakeCase,
          autoplay: playVideoProps.autoplay ?? true,
          ...playVideoProps,
        },
      }

      setLaunchVideoParams(params)
    },
    [user, locale.codeSnakeCase, isScriptLoaded]
  )

  useEffect(() => {
    const launch = async () => {
      const instanceId = await window.WebVideoPlayerDOM.launch(launchVideoParams!)
      if (instanceId) setPlayerInstanceId(instanceId)
    }

    if (
      !!containerElement &&
      !playerInstanceId &&
      launchVideoParams &&
      !launchVideoPlayerPromise.current
    ) {
      launchVideoPlayerPromise.current = launch()
    }
  }, [launchVideoParams, playerInstanceId, containerElement])

  useEffect(() => {
    return () => {
      if (playerInstanceId && window.WebVideoPlayerDOM?.destroy) {
        window.WebVideoPlayerDOM.destroy(playerInstanceId)
      }
    }
  }, [playerInstanceId])

  const value: VideoPlayerContextType = {
    playVideo,
    setScriptUrl,
  }

  return (
    <VideoPlayerContext.Provider value={value}>
      {children}
      <Dialog open={!!launchVideoParams} onClose={() => closePlayer()} className="relative z-50">
        <div className="fixed inset-0 z-10 bg-black/80" aria-hidden="true">
          <div className="fixed inset-0 flex items-center justify-center">
            <DialogPanel
              id={VIDEO_PLAYER_CONTAINER_ID}
              className="relative h-screen w-screen bg-bodi-black-text"
            />
          </div>
          <CloseButton as={ClosePlayer} onClick={() => closePlayer()} />
        </div>
      </Dialog>
      {scriptUrl && <Script async onReady={() => setIsScriptLoaded(true)} src={scriptUrl} />}
    </VideoPlayerContext.Provider>
  )
}

export const useVideoPlayer = (scriptUrl?: string) => {
  const context = useContext(VideoPlayerContext)
  if (!context) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider')
  }

  if (scriptUrl && scriptUrl !== configPublic.videoPlayer.scriptUrl) {
    context.setScriptUrl(scriptUrl)
  }
  return context
}

const ClosePlayer = ({ onClick }: { onClick: () => void }) => (
  <Image
    src={CloseWhite}
    onClick={onClick}
    title="close"
    alt="close"
    className="absolute z-[99] mt-8 ml-7 h-15 w-[8%] cursor-pointer border-0 bg-no-repeat p-2.5"
  />
)
